import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useEvTripsStore } from '@/store/evTrips'
import { showToast } from '@/helpers/messages'

import CommonError from '@/components/CommonError.vue'
import DetailsSection from './DetailsSection.vue'
import PriceBreakDownSection from './PriceBreakDownSection.vue'
import FinanceSection from './FinanceSection.vue'
import EvTripDamageReport from './EvTripDamageReport.vue'
import TripImagesTab from './TripImagesTab.vue'
import TripLogsTab from './TripLogsTab.vue'
import PortalPaperForEvTrip from './PortalPaperForEvTrip.vue'
import Loader from '@/components/Loader.vue'
import type { EvTripExtended } from '@/models/evTrip'

type TabName = typeof TAB_NAMES[number]


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    id: {}
  },
  setup(__props: any) {

const props = __props

const route = useRoute()
const router = useRouter()
const evTripsStore = useEvTripsStore()

const TAB_NAMES = [
  'trip-info',
  'trip-images',
  'trip-damage-report',
  'finance',
  'trip-discrepencies',
  'trip-logs',
  'portal-paper',
] as const
const normalizeTab = (val: unknown): TabName => {
  const s = Array.isArray(val) ? val[0] : String(val ?? '')
  if ((TAB_NAMES as readonly string[]).includes(s)) {
    return s as TabName
  }
  const saved =
    localStorage.getItem(`ev-trip-details-tab-${props.id}`) ||
    localStorage.getItem('ev-trip-details-active-tab')
  if (saved && (TAB_NAMES as readonly string[]).includes(saved)) {
    return saved as TabName
  }
  return 'trip-info'
}

const activeTab = ref<TabName>(normalizeTab(route.query.tab))

// URL -> UI
watch(
  () => route.query.tab,
  (q) => {
    const next = normalizeTab(q)
    if (next !== activeTab.value) activeTab.value = next
  }
)

// UI -> URL & localStorage (replace, щоб не плодити історію)
watch(
  activeTab,
  (t) => {
    localStorage.setItem(`ev-trip-details-tab-${props.id}`, t)
    localStorage.setItem('ev-trip-details-active-tab', t)
    const current = Array.isArray(route.query.tab) ? route.query.tab[0] : route.query.tab
    if (current !== t) {
      router.replace({ path: route.path, query: { ...route.query, tab: t } })
    }
  },
  { immediate: true }
)

const trip = computed(
  () => evTripsStore.tripsDetails[props.id]?.data as EvTripExtended | null
)
const error = computed(() => evTripsStore.tripsDetails[props.id]?.error)
const isLoading = computed(() => evTripsStore.tripsDetails[props.id]?.isLoading)

// A failed reload keeps the already loaded data and only shows a toast.
watch(error, e => {
  if (e && trip.value) showToast({ type: 'error', message: e })
})

const getEvTripDetails = () => {
  evTripsStore.getEvTripDetails(props.id)
}

onMounted(() => {
  if (!isLoading.value) getEvTripDetails()
})

return (_ctx: any,_cache: any) => {
  const _component_RenterIdentificationSection = _resolveComponent("RenterIdentificationSection")!
  const _component_el_scrollbar = _resolveComponent("el-scrollbar")!
  const _component_el_tab_pane = _resolveComponent("el-tab-pane")!
  const _component_el_tabs = _resolveComponent("el-tabs")!
  const _component_el_container = _resolveComponent("el-container")!

  return (_openBlock(), _createBlock(_component_el_container, {
    direction: "vertical",
    class: "container"
  }, {
    default: _withCtx(() => [
      (error.value && !trip.value)
        ? (_openBlock(), _createBlock(CommonError, {
            key: 0,
            errorMessage: error.value,
            onRetryPress: getEvTripDetails
          }, null, 8, ["errorMessage"]))
        : (!trip.value)
          ? (_openBlock(), _createBlock(Loader, { key: 1 }))
          : (_openBlock(), _createBlock(_component_el_tabs, {
              key: 2,
              modelValue: activeTab.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeTab).value = $event)),
              type: "card",
              class: "ev-trip-tabs"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_tab_pane, {
                  label: "Trip info",
                  name: "trip-info"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_scrollbar, { always: "" }, {
                      default: _withCtx(() => [
                        _createVNode(DetailsSection, { trip: trip.value }, null, 8, ["trip"]),
                        _createVNode(_component_RenterIdentificationSection, {
                          renter: trip.value.renter
                        }, null, 8, ["renter"]),
                        _createVNode(PriceBreakDownSection, {
                          trip: trip.value,
                          to_calculate_parameter_list: trip.value?.to_calculate_parameter_list
                        }, null, 8, ["trip", "to_calculate_parameter_list"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_tab_pane, {
                  name: "trip-images",
                  label: "Trip images",
                  lazy: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(TripImagesTab, { trip: trip.value }, null, 8, ["trip"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_tab_pane, {
                  name: "trip-damage-report",
                  label: "Damage report",
                  lazy: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_scrollbar, { always: "" }, {
                      default: _withCtx(() => [
                        _createVNode(EvTripDamageReport, { trip: trip.value }, null, 8, ["trip"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_tab_pane, {
                  name: "finance",
                  label: "Finance",
                  lazy: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_scrollbar, { always: "" }, {
                      default: _withCtx(() => [
                        _createVNode(FinanceSection, { trip: trip.value }, null, 8, ["trip"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_tab_pane, {
                  name: "trip-discrepencies",
                  label: "Trip discrepencies",
                  lazy: ""
                }),
                _createVNode(_component_el_tab_pane, {
                  name: "trip-logs",
                  label: "Trip logs",
                  lazy: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(TripLogsTab, { trip: trip.value }, null, 8, ["trip"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_tab_pane, {
                  name: "portal-paper",
                  label: "Portal Paper",
                  lazy: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_scrollbar, { always: "" }, {
                      default: _withCtx(() => [
                        _createVNode(PortalPaperForEvTrip, { trip: trip.value }, null, 8, ["trip"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["modelValue"]))
    ]),
    _: 1
  }))
}
}

})